import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "../../lib/utils"

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  showScrollIndicator?: boolean;
  showScrollHint?: boolean;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, showScrollIndicator = true, showScrollHint = true, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);

  React.useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
        setCanScroll(scrollHeight > clientHeight);
        setIsScrolled(scrollTop > 0);
        setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight - 5); // 5px threshold
      }
    };

    // Check on mount and whenever content changes
    checkScroll();
    
    // Set up ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(checkScroll);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setIsScrolled(scrollTop > 0);
    setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight - 5); // 5px threshold
  };

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className="h-full w-full rounded-[inherit] [&>div]:!h-full"
        ref={contentRef}
        onScroll={handleScroll}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
      
      {/* Top shadow when scrolled down */}
      {showScrollIndicator && canScroll && (
        <div className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b",
          "from-brand-background to-transparent dark:from-brand-background",
          "transition duration-200 mr-4",
          isScrolled ? "opacity-100" : "opacity-0"
        )}/>
      )}
      
      {/* Bottom shadow when not at the bottom */}
      {showScrollIndicator && canScroll && (
        <div className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t",
          "from-brand-background to-transparent dark:from-brand-background",
          "transition duration-200 mr-4",
          !isScrolledToBottom ? "opacity-100" : "opacity-0"
        )}/>
      )}
      
      {/* Down arrow when not scrolled to bottom */}
      {showScrollHint && canScroll && !isScrolledToBottom && !isScrolled && (
        <div className={cn(
          "absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center justify-center",
          "transition-opacity duration-200 opacity-30 hover:opacity-60 pr-4"
        )}>
          <ChevronDown className="h-4 w-4 text-muted-foreground dark:text-muted-foreground" />
        </div>
      )}

      {/* Up arrow when scrolled to bottom */}
      {showScrollHint && canScroll && isScrolledToBottom && isScrolled && (
        <div className={cn(
          "absolute top-3 left-1/2 -translate-x-1/2 flex items-center justify-center",
          "transition-opacity duration-200 opacity-30 hover:opacity-60"
        )}>
          <ChevronUp className="h-4 w-4 text-muted-foreground dark:text-muted-foreground" />
        </div>
      )}
    </ScrollAreaPrimitive.Root>
  )
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-3 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-3 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb 
      className={cn(
        "relative flex-1 rounded-full",
        "bg-brand-dark hover:bg-brand-dark dark:bg-brand-dark dark:hover:bg-brand-dark",
        "transition-colors duration-150"
      )} 
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar } 